import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { legalDocs, LegalDocId } from '../../data/legal';
interface LegalDocModalProps {
  doc: LegalDocId;
  onClose: () => void;
}

/** Reads a legal document in place, so agreeing never means losing your progress. */
export function LegalDocModal({
  doc,
  onClose
}: LegalDocModalProps) {
  const d = legalDocs[doc];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="legal-doc-title" data-id="element-2576" data-mp="3x6rlvmwzmym">
      <motion.div className="absolute inset-0 bg-dark-navy/40" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15
    }} onClick={onClose} data-id="element-2577" data-mp="1y9o90ml2nsvc" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.97,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full max-w-xl max-h-[85dvh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden" data-id="element-2578" data-mp="2qkjd0iu5p2d0">
        <div className="px-7 pt-6 pb-4 flex items-start justify-between gap-4 border-b border-slate-100" data-id="element-2579" data-mp="2lmhtjwstj110">
          <div data-id="element-2580" data-mp="39ckfrwa1xcrp">
            <h2 id="legal-doc-title" className="font-display font-bold text-xl text-dark-navy" data-id="element-2581" data-mp="39e53p31v1xgl">
              {d.title}
            </h2>
            <p className="text-[12px] text-slate-400 mt-1" data-id="element-2582" data-mp="30j8kdhoff4f0">
              Version {d.version} · Effective {d.effective}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-2583" data-mp="3sa4w2eyr330p">
            <XIcon className="w-4 h-4" data-id="element-2584" data-mp="apmwuou8hpis" />
          </button>
        </div>

        <div className="px-7 py-5 overflow-y-auto" data-id="element-2585" data-mp="6qvv24etpjpc">
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-2586" data-mp="1r18ue5ztiam">{d.intro}</p>
          <div className="mt-5 space-y-5" data-id="element-2587" data-mp="1t46nkde0fpw">
            {d.sections.map(s => <section key={s.heading} data-id="element-2588" data-mp="3szorungrgtf0">
                <h3 className="text-sm font-semibold text-dark-navy" data-id="element-2589" data-mp="3nz13k9jx2qoh">
                  {s.heading}
                </h3>
                <p className="text-[13.5px] text-slate-600 leading-relaxed mt-1" data-id="element-2590" data-mp="3j6q1p15m8uta">
                  {s.body}
                </p>
              </section>)}
          </div>
        </div>

        <div className="px-7 py-4 border-t border-slate-100 flex justify-end" data-id="element-2591" data-mp="3a85q0jyh3lek">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-dark-navy text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-150 ease-out" data-id="element-2592" data-mp="358jce9eqg358">
            Close
          </button>
        </div>
      </motion.div>
    </div>;
}